import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Bot, TrendingUp, UtensilsCrossed, CalendarDays, Lightbulb, Mail, MessageCircle, Plus, Trash2, Loader2, ChevronDown, Truck, Star, Clock, Scale, Copy, Check, MailPlus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AGENT_CATEGORIES = [
  {
    label: 'Gestion',
    agents: [
      { id: 'analyste', label: 'Analyste', icon: TrendingUp, color: '#4285F4', desc: 'CA, marges, tendances' },
      { id: 'menu', label: 'Menu', icon: UtensilsCrossed, color: '#34A853', desc: 'Food cost, prix, carte' },
      { id: 'reservations', label: 'Reservations', icon: CalendarDays, color: '#C19C7B', desc: 'Frequentation, patterns' },
      { id: 'fournisseurs', label: 'Fournisseurs', icon: Truck, color: '#8B5CF6', desc: 'Commandes, negociation' },
    ]
  },
  {
    label: 'Communication',
    agents: [
      { id: 'email', label: 'Email', icon: Mail, color: '#EA4335', desc: 'Rediger & envoyer mails' },
      { id: 'communication', label: 'WhatsApp', icon: MessageCircle, color: '#25D366', desc: 'Reponses messages' },
      { id: 'avis', label: 'Avis Google', icon: Star, color: '#F59E0B', desc: 'Repondre aux avis' },
    ]
  },
  {
    label: 'Organisation',
    agents: [
      { id: 'strategie', label: 'Strategie', icon: Lightbulb, color: '#FBBC04', desc: 'Conseils business' },
      { id: 'planning', label: 'Planning', icon: Clock, color: '#06B6D4', desc: 'Planning equipe' },
      { id: 'juridique', label: 'Juridique', icon: Scale, color: '#64748B', desc: 'RH, reglementation' },
    ]
  },
];

const ALL_AGENTS = AGENT_CATEGORIES.flatMap(c => c.agents);

const SUGGESTIONS = {
  analyste: [
    "Quel est mon CA ce mois-ci par rapport au mois dernier ?",
    "Quels sont mes jours les plus rentables ?",
    "Analyse mes marges des 30 derniers jours",
  ],
  menu: [
    "Quels plats ont la meilleure marge ?",
    "Quels plats devrais-je retirer de la carte ?",
    "Propose-moi 3 nouveaux plats rentables",
  ],
  reservations: [
    "Quels sont mes jours creux cette semaine ?",
    "Combien de no-shows ce mois ?",
    "Quels clients VIP arrivent bientot ?",
  ],
  strategie: [
    "Comment augmenter mon CA de 10% ?",
    "Quel est mon point mort mensuel ?",
    "Propose un plan pour la saison estivale",
  ],
  email: [
    "Redige une reponse a un client qui veut reserver pour 20 personnes",
    "Ecris un email de negociation a mon fournisseur Metro",
    "Redige une newsletter pour la saint-valentin",
  ],
  communication: [
    "Un client demande les horaires d'ouverture",
    "Quelqu'un veut reserver pour ce soir 4 personnes",
    "Un client demande si on a des options vegetariennes",
  ],
  fournisseurs: [
    "Redige un bon de commande pour Metro (fruits de mer)",
    "Ecris une reclamation pour livraison incomplete",
    "Compare les prix huile d'olive entre 3 fournisseurs",
  ],
  avis: [
    "Reponds a cet avis 5 etoiles : 'Excellent repas, cadre magnifique !'",
    "Reponds a cet avis 2 etoiles : 'Service lent, plats froids'",
    "Reponds a cet avis : 'Tres bon mais un peu cher pour la quantite'",
  ],
  planning: [
    "Propose un planning pour la semaine prochaine",
    "Comment gerer si un serveur est absent samedi soir ?",
    "Optimise les effectifs pour un lundi creux",
  ],
  juridique: [
    "Quels sont les affichages obligatoires en restaurant ?",
    "Regles pour un CDD saisonnier en restauration ?",
    "Normes HACCP : quelles temperatures de conservation ?",
  ],
};

export default function IACowork() {
  const [activeAgent, setActiveAgent] = useState('analyste');
  const [sessions, setSessions] = useState({});
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAgents, setShowAgents] = useState(false);
  const [copied, setCopied] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [emailSent, setEmailSent] = useState(null);
  const messagesEnd = useRef(null);
  const inputRef = useRef(null);

  const currentSession = sessions[activeAgent] || { id: null, messages: [] };

  const scrollToBottom = useCallback(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [currentSession.messages, scrollToBottom]);
  useEffect(() => { inputRef.current?.focus(); }, [activeAgent]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };

    setSessions(prev => ({
      ...prev,
      [activeAgent]: {
        ...prev[activeAgent],
        messages: [...(prev[activeAgent]?.messages || []), userMsg]
      }
    }));
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/ai-agents/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent: activeAgent,
          message: text,
          session_id: currentSession.id
        })
      });
      const data = await res.json();

      setSessions(prev => ({
        ...prev,
        [activeAgent]: {
          id: data.session_id,
          messages: [
            ...(prev[activeAgent]?.messages || []),
            { role: 'assistant', content: data.response, timestamp: new Date().toISOString() }
          ]
        }
      }));
    } catch {
      setSessions(prev => ({
        ...prev,
        [activeAgent]: {
          ...prev[activeAgent],
          messages: [
            ...(prev[activeAgent]?.messages || []),
            { role: 'assistant', content: "Erreur de connexion. Reessayez.", timestamp: new Date().toISOString() }
          ]
        }
      }));
    }
    setLoading(false);
  };

  const newSession = () => {
    setSessions(prev => ({ ...prev, [activeAgent]: { id: null, messages: [] } }));
  };

  const clearSession = async () => {
    if (currentSession.id) {
      try {
        await fetch(`${API}/api/ai-agents/sessions/${currentSession.id}`, { method: 'DELETE' });
      } catch {}
    }
    newSession();
  };

  const copyText = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopied(idx);
    setTimeout(() => setCopied(null), 2000);
  };

  const sendEmailDirect = async (content, idx) => {
    // Extract OBJET and body from the AI response
    const lines = content.split('\n');
    let subject = '';
    let body = '';
    let inBody = false;

    for (const line of lines) {
      if (line.startsWith('OBJET:') || line.startsWith('Objet:') || line.startsWith('Objet :')) {
        subject = line.replace(/^OBJET:\s*|^Objet:\s*|^Objet :\s*/i, '').trim();
      } else if (line.trim() === '---') {
        inBody = !inBody;
      } else if (inBody || (!subject && !line.startsWith('OBJET'))) {
        body += line + '\n';
      }
    }

    if (!body.trim()) body = content;
    if (!subject) subject = 'Message du restaurant Le Mas';

    const to = prompt('Adresse email du destinataire :');
    if (!to || !to.includes('@')) return;

    setSendingEmail(idx);
    try {
      const res = await fetch(`${API}/api/ai-agents/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, subject, body_html: body.trim() })
      });
      if (res.ok) {
        setEmailSent(idx);
        setTimeout(() => setEmailSent(null), 3000);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Erreur envoi email');
      }
    } catch {
      alert('Erreur de connexion');
    }
    setSendingEmail(null);
  };

  const agent = ALL_AGENTS.find(a => a.id === activeAgent);

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] max-h-[calc(100vh-80px)]" data-testid="ia-cowork-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: agent.color + '18' }}>
            <Bot size={13} style={{ color: agent.color }} />
          </div>
          <div>
            <h1 className="text-sm font-semibold text-[rgb(var(--lm-text))]">IA Cowork</h1>
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={newSession} className="p-1 rounded hover:bg-[rgb(var(--lm-card))] transition-colors" title="Nouvelle conversation" data-testid="new-session-btn">
            <Plus size={13} className="text-[rgb(var(--lm-text-secondary))]" />
          </button>
          <button onClick={clearSession} className="p-1 rounded hover:bg-[rgb(var(--lm-card))] transition-colors" title="Supprimer" data-testid="clear-session-btn">
            <Trash2 size={13} className="text-[rgb(var(--lm-text-muted))]" />
          </button>
        </div>
      </div>

      {/* Agent Tabs - Desktop: compact single row */}
      <div className="mb-2">
        <div className="hidden md:flex gap-0.5 p-0.5 bg-[rgb(var(--lm-card))] rounded-md">
          {ALL_AGENTS.map(a => (
            <button key={a.id} onClick={() => setActiveAgent(a.id)}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[9px] font-medium transition-all ${
                activeAgent === a.id
                  ? 'bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text))] shadow-sm'
                  : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text-secondary))]'
              }`}
              data-testid={`agent-tab-${a.id}`}
            >
              <a.icon size={10} style={activeAgent === a.id ? { color: a.color } : {}} />
              {a.label}
            </button>
          ))}
        </div>

        {/* Mobile dropdown with categories */}
        <div className="md:hidden relative">
          <button onClick={() => setShowAgents(!showAgents)}
            className="flex items-center justify-between w-full p-2.5 bg-[rgb(var(--lm-card))] rounded-lg"
            data-testid="agent-dropdown"
          >
            <div className="flex items-center gap-2">
              <agent.icon size={14} style={{ color: agent.color }} />
              <span className="text-xs font-medium text-[rgb(var(--lm-text))]">{agent.label}</span>
              <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">{agent.desc}</span>
            </div>
            <ChevronDown size={14} className={`text-[rgb(var(--lm-text-muted))] transition-transform ${showAgents ? 'rotate-180' : ''}`} />
          </button>
          {showAgents && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-[rgb(var(--lm-card))] rounded-lg border border-[rgb(var(--lm-border))] shadow-lg z-20 max-h-80 overflow-y-auto">
              {AGENT_CATEGORIES.map(cat => (
                <div key={cat.label}>
                  <div className="px-3 py-1.5 text-[9px] font-bold text-[rgb(var(--lm-text-muted))] uppercase tracking-wider bg-[rgb(var(--lm-bg))]/50">
                    {cat.label}
                  </div>
                  {cat.agents.map(a => (
                    <button key={a.id} onClick={() => { setActiveAgent(a.id); setShowAgents(false); }}
                      className={`flex items-center gap-2 w-full px-3 py-2 text-left text-xs transition-colors ${
                        activeAgent === a.id ? 'bg-[rgb(var(--lm-bg))]' : 'hover:bg-[rgb(var(--lm-bg))]'
                      }`}
                    >
                      <a.icon size={12} style={{ color: a.color }} />
                      <span className="font-medium text-[rgb(var(--lm-text))]">{a.label}</span>
                      <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">{a.desc}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto rounded-lg bg-[rgb(var(--lm-card))]/50 border border-[rgb(var(--lm-border))]/30 p-3 space-y-3" data-testid="messages-area">
        {currentSession.messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: agent.color + '15' }}>
              <agent.icon size={22} style={{ color: agent.color }} />
            </div>
            <h3 className="text-sm font-medium text-[rgb(var(--lm-text))] mb-1">Agent {agent.label}</h3>
            <p className="text-[11px] text-[rgb(var(--lm-text-muted))] mb-4">{agent.desc}</p>
            <div className="grid gap-2 w-full max-w-md">
              {(SUGGESTIONS[activeAgent] || []).map((s, i) => (
                <button key={i} onClick={() => sendMessage(s)}
                  className="text-left text-[11px] px-3 py-2 rounded-lg bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))]/40 text-[rgb(var(--lm-text-secondary))] hover:border-[rgb(var(--lm-border))] transition-colors"
                  data-testid={`suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          currentSession.messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] group relative ${
                msg.role === 'user'
                  ? 'bg-[rgb(var(--lm-text))] text-[rgb(var(--lm-bg))]'
                  : 'bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text))] border border-[rgb(var(--lm-border))]/30'
              } rounded-xl px-3 py-2`}>
                {msg.role === 'assistant' && (
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <agent.icon size={10} style={{ color: agent.color }} />
                      <span className="text-[9px] font-medium" style={{ color: agent.color }}>{agent.label}</span>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => copyText(msg.content, i)}
                        className="p-0.5 rounded hover:bg-[rgb(var(--lm-border))]/20"
                        title="Copier"
                        data-testid={`copy-msg-${i}`}
                      >
                        {copied === i ? <Check size={10} className="text-green-500" /> : <Copy size={10} className="text-[rgb(var(--lm-text-muted))]" />}
                      </button>
                      {(activeAgent === 'email' || activeAgent === 'fournisseurs' || activeAgent === 'avis') && (
                        <button onClick={() => sendEmailDirect(msg.content, i)}
                          disabled={sendingEmail === i}
                          className="p-0.5 rounded hover:bg-[rgb(var(--lm-border))]/20"
                          title="Envoyer par email"
                          data-testid={`send-email-${i}`}
                        >
                          {emailSent === i ? <Check size={10} className="text-green-500" /> :
                           sendingEmail === i ? <Loader2 size={10} className="animate-spin text-[rgb(var(--lm-text-muted))]" /> :
                           <MailPlus size={10} className="text-[rgb(var(--lm-text-muted))]" />}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                <div className="text-[12px] leading-relaxed whitespace-pre-wrap">{msg.content}</div>
              </div>
            </div>
          ))
        )}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))]/30 rounded-xl px-3 py-2 flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" style={{ color: agent.color }} />
              <span className="text-[11px] text-[rgb(var(--lm-text-muted))]">Reflexion...</span>
            </div>
          </div>
        )}
        <div ref={messagesEnd} />
      </div>

      {/* Input */}
      <div className="mt-3 flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendMessage(input)}
          placeholder={`Parlez a l'agent ${agent.label}...`}
          className="flex-1 bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/40 rounded-lg px-3 py-2.5 text-xs text-[rgb(var(--lm-text))] placeholder:text-[rgb(var(--lm-text-muted))] focus:outline-none focus:border-[rgb(var(--lm-border))]"
          disabled={loading}
          data-testid="agent-input"
        />
        <button
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || loading}
          className="px-3 py-2.5 rounded-lg text-white transition-all disabled:opacity-30"
          style={{ background: agent.color }}
          data-testid="agent-send-btn"
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}
